import { Injectable, ErrorHandler, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

/**
 * Log entry structure
 */
export interface LogEntry {
  timestamp: string;
  level: 'log' | 'info' | 'warn' | 'error' | 'debug';
  message: string;
  data?: any;
  stack?: string;
}

/**
 * Error report payload sent to the server
 */
export interface ErrorReport {
  sessionId: string;
  userId?: string;
  userAgent: string;
  url: string;
  timestamp: string;
  prevLogTime: string;
  error: {
    message: string;
    stack?: string;
    type?: string;
  };
  consoleLogs: LogEntry[];
  metadata?: Record<string, any>;
}

/**
 * Configuration options for DebugLoggingService
 */
export interface DebugLoggingConfig {
  /** Endpoint URL to send error reports */
  endpoint?: string;
  /** Whether to intercept console methods */
  interceptConsole?: boolean;
  /** Whether to capture unhandled errors */
  captureUnhandledErrors?: boolean;
  /** Whether to capture unhandled promise rejections */
  captureUnhandledRejections?: boolean;
  /** Whether to send reports automatically on error */
  autoSendOnError?: boolean;
  /** Custom headers for HTTP requests */
  headers?: Record<string, string>;
  /** Function to get current user ID */
  getUserId?: () => string | undefined;
  /** Function to get additional metadata */
  getMetadata?: () => Record<string, any>;
  /** Whether logging is enabled */
  enabled?: boolean;
  /** Log levels to capture */
  captureLevels?: ('log' | 'info' | 'warn' | 'error' | 'debug')[];
}

/**
 * DebugLoggingService - A standalone, independent logging service for Angular applications
 *
 * Features:
 * - Intercepts all console output (log, warn, error, info, debug)
 * - Captures unhandled errors and promise rejections
 * - Maintains a circular buffer of logs
 * - Sends complete console history when an error occurs
 * - Fully configurable and can work with any Angular project
 *
 * Usage:
 * 1. Import and provide in your module
 * 2. Configure with your endpoint and options
 * 3. Initialize in your app component
 *
 * @example
```ts
 // In app.module.ts
 providers: [
   DebugLoggingService,
   { provide: ErrorHandler, useClass: GlobalErrorHandler }
 ]
 
 // In app.component.ts
 constructor(private debugLogging: DebugLoggingService) {
   this.debugLogging.configure({
     endpoint: 'https://logger.mysembra.com/',
     autoSendOnError: true
   });
   this.debugLogging.initialize();
 }
```
 */
@Injectable({
  providedIn: 'root'
})
export class DebugLoggingService {
  private prevLogTime: string = '0';
  private logBuffer: LogEntry[] = [];
  private sessionId: string;
  private config: DebugLoggingConfig = {
    endpoint: undefined,
    interceptConsole: true,
    captureUnhandledErrors: true,
    captureUnhandledRejections: true,
    autoSendOnError: true,
    headers: {},
    getUserId: () => undefined,
    getMetadata: () => ({}),
    enabled: true,
    captureLevels: ['log', 'info', 'warn', 'error', 'debug']
  };

  private originalConsole: {
    log: typeof console.log;
    info: typeof console.info;
    warn: typeof console.warn;
    error: typeof console.error;
    debug: typeof console.debug;
  };

  private initialized = false;
  private boundErrorHandler: ((event: ErrorEvent) => void) | null = null;
  private boundRejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone
  ) {
    // Generate unique session ID
    this.sessionId = this.generateSessionId();

    // Store original console methods
    this.originalConsole = {
      log: console.log.bind(console),
      info: console.info.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      debug: console.debug.bind(console)
    };
  }

  /**
   * Configure the logging service
   * @param config Configuration options
   */
  configure(config: Partial<DebugLoggingConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Ensure that the backend is up and ready to receive logs
   */
  initializeForAPIHealth(): void {
    
      this.ngZone.runOutsideAngular(() => {
        this.http.get(this.config.endpoint + '/health')
          .subscribe({
            next: () => {
              if (!this.config.enabled) {
                    return;
                  }

              this.originalConsole.info('[DebugLoggingService] API is healthy completing initialization');
                  if (this.initialized) {
                    this.originalConsole.warn('[DebugLoggingService] Already initialized');
                    return;
                  }     

                  if (this.config.interceptConsole) {
                    this.interceptConsoleMethods();
                  }

                  if (this.config.captureUnhandledErrors) {
                    this.setupGlobalErrorHandler();
                  }

                  if (this.config.captureUnhandledRejections) {
                    this.setupUnhandledRejectionHandler();
                  }

                  this.initialized = true;
                  this.addLogEntry('info', '[DebugLoggingService] Initialized');

              
            },
            error: (err) => {
              this.originalConsole.error('[DebugLoggingService] API is unhealthy not initializing call window.debugService.initializeForAPIHealth(); to try again:', err);
              
            }
          });
      });
  }

  /**
   * Initialize the logging service - call this once in your app component
   */
  initialize(): void {
    window["debugService"] = this;

    window["debugService"].initializeForAPIHealth();
  }

  /**
   * Manually add a log entry
   */
  log(message: string, data?: any): void {
    this.addLogEntry('log', message, data);
  }

  /**
   * Manually add an info entry
   */
  info(message: string, data?: any): void {
    this.addLogEntry('info', message, data);
  }

  /**
   * Manually add a warning entry
   */
  warn(message: string, data?: any): void {
    this.addLogEntry('warn', message, data);
  }

  /**
   * Manually add an error entry
   */
  error(message: string, data?: any, stack?: string): void {
    this.addLogEntry('error', message, data, stack);
  }

  /**
   * Manually add a debug entry
   */
  debug(message: string, data?: any): void {
    this.addLogEntry('debug', message, data);
  }

  /**
   * Capture an error and optionally send report
   * @param error The error to capture
   * @param autoSend Whether to automatically send the report (uses config if not specified)
   */
  captureError(error: Error | string, autoSend?: boolean): void {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    this.addLogEntry('error', errorObj.message, undefined, errorObj.stack);

    const shouldSend = autoSend ?? this.config.autoSendOnError;
    if (shouldSend && this.config.endpoint) {
      this.sendErrorReport(errorObj);
    }
  }

  /**
   * Manually send an error report with all buffered logs
   * @param error Optional error to include in the report
   */
  sendErrorReport(error?: Error): Promise<void> {
    if (!this.config.endpoint) {
      this.originalConsole.warn('[DebugLoggingService] No endpoint configured, cannot send report');
      return Promise.resolve();
    }

    const report = this.buildErrorReport(error);

  

    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        ...this.config.headers
      });

      // Run outside Angular zone to avoid triggering change detection
      this.ngZone.runOutsideAngular(() => {
        this.http.post(this.config.endpoint + '/logs', report, { headers })
          .subscribe({
            next: () => {
              this.originalConsole.info('[DebugLoggingService] Error report sent successfully');
              this.clearLogs();
              this.prevLogTime = report.timestamp;

              resolve();
            },
            error: (err) => {
              this.originalConsole.error('[DebugLoggingService] Failed to send error report:', err);
              reject(err);
            }
          });
      });
    });
  }

  /**
   * Get a copy of the current log buffer
   */
  getLogs(): LogEntry[] {
    return [...this.logBuffer];
  }

  /**
   * Clear the log buffer
   */
  clearLogs(): void {
    this.logBuffer = [];
  }

  /**
   * Get the current session ID
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Build the error report payload
   */
  private buildErrorReport(error?: Error): ErrorReport {
    return {
      sessionId: this.sessionId,
      userId: this.config.getUserId?.(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      prevLogTime: this.prevLogTime.toString() || 'N/A',
      timestamp: new Date().toISOString(),
      error: {
        message: error?.message || 'Manual report',
        stack: error?.stack,
        type: error?.name
      },
      consoleLogs: this.getLogs(),
      metadata: this.config.getMetadata?.()
    };
  }

  /**
   * Intercept console methods to capture all output
   */
  private interceptConsoleMethods(): void {
    const levels: ('log' | 'info' | 'warn' | 'error' | 'debug')[] = ['log', 'info', 'warn', 'error', 'debug'];

    levels.forEach(level => {
      if (this.config.captureLevels?.includes(level)) {
        console[level] = (...args: any[]) => {
          // Call original console method
          this.originalConsole[level](...args);

          // Capture stack trace for every log entry
          const stack = this.captureStackTrace();

          // Add to buffer
          const message = args.map(arg => this.stringify(arg)).join(' ');
          const errorArg = args.find(arg => arg instanceof Error);
          const errorStack = errorArg?.stack;

          this.addLogEntry(level, message, args.length > 1 ? args : args[0], errorStack || stack);

          // Auto-send on error if configured
          if (level === 'error' && this.config.autoSendOnError && this.config.endpoint) {
            this.sendErrorReport(errorArg);
          }
        };
      }
    });
  }

  /**
   * Capture stack trace at the point of logging
   */
  private captureStackTrace(): string {
    const err = new Error();
    const stack = err.stack || '';
    // Remove the first 3 lines (Error, captureStackTrace, intercepted console method)
    const lines = stack.split('\n');
    return lines.slice(3).join('\n');
  }

  /**
   * Setup global error handler for uncaught exceptions
   */
  private setupGlobalErrorHandler(): void {
    this.boundErrorHandler = (event: ErrorEvent) => {
      this.addLogEntry('error', `Uncaught Error: ${event.message}`, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }, event.error?.stack);

      if (this.config.autoSendOnError && this.config.endpoint) {
        this.sendErrorReport(event.error || new Error(event.message));
      }
    };

    window.addEventListener('error', this.boundErrorHandler);
  }

  /**
   * Setup handler for unhandled promise rejections
   */
  private setupUnhandledRejectionHandler(): void {
    this.boundRejectionHandler = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = reason instanceof Error ? reason.message : String(reason);
      const stack = reason instanceof Error ? reason.stack : undefined;

      this.addLogEntry('error', `Unhandled Promise Rejection: ${message}`, reason, stack);

      if (this.config.autoSendOnError && this.config.endpoint) {
        const error = reason instanceof Error ? reason : new Error(message);
        this.sendErrorReport(error);
      }
    };

    window.addEventListener('unhandledrejection', this.boundRejectionHandler);
  }

  /**
   * Add an entry to the log buffer
   */
  private addLogEntry(level: LogEntry['level'], message: string, data?: any, stack?: string): void {
    if (!this.config.enabled) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data: this.sanitizeData(data),
      stack
    };

    this.logBuffer.push(entry);
  }

  /**
   * Convert any value to a string for logging
   */
  private stringify(value: any): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return value;
    if (value instanceof Error) return `${value.name}: ${value.message}`;

    try {
      return JSON.stringify(value, this.getCircularReplacer(), 2);
    } catch {
      return String(value);
    }
  }

  /**
   * Sanitize data for safe JSON serialization
   */
  private sanitizeData(data: any): any {
    if (data === null || data === undefined) return data;
    if (typeof data !== 'object') return data;

    try {
      // Test if it can be serialized
      JSON.stringify(data, this.getCircularReplacer());
      return data;
    } catch {
      return String(data);
    }
  }

  /**
   * Handle circular references in JSON stringify
   */
  private getCircularReplacer(): (key: string, value: any) => any {
    const seen = new WeakSet();
    return (key: string, value: any) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular Reference]';
        }
        seen.add(value);
      }
      // Handle special types
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack
        };
      }
      if (typeof value === 'function') {
        return '[Function]';
      }
      if (value instanceof HTMLElement) {
        return `[HTMLElement: ${value.tagName}]`;
      }
      return value;
    };
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${randomPart}`;
  }

  /**
   * Restore original console methods and remove event listeners
   * Call this when destroying the service (e.g., in tests)
   */
  destroy(): void {
    if (this.config.interceptConsole) {
      console.log = this.originalConsole.log;
      console.info = this.originalConsole.info;
      console.warn = this.originalConsole.warn;
      console.error = this.originalConsole.error;
      console.debug = this.originalConsole.debug;
    }

    if (this.boundErrorHandler) {
      window.removeEventListener('error', this.boundErrorHandler);
    }

    if (this.boundRejectionHandler) {
      window.removeEventListener('unhandledrejection', this.boundRejectionHandler);
    }

    this.initialized = false;
    this.clearLogs();
  }
}

/**
 * Global Error Handler that integrates with DebugLoggingService
 *
 * Usage:
 * In app.module.ts:
 * providers: [
 *   { provide: ErrorHandler, useClass: GlobalErrorHandler }
 * ]
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private debugLogging: DebugLoggingService) {}

  handleError(error: any): void {
    // Extract the actual error if it's wrapped
    const actualError = error.rejection || error.originalError || error;

    // Capture through the debug logging service
    this.debugLogging.captureError(actualError);

    // Also log to console (the intercepted version will capture it)
    console.error('Application Error:', actualError);
  }
}
